import React, { useEffect, useState, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Card,
  CardMedia,
  IconButton,
} from '@mui/material';
import { People, School, Add, Delete, PhotoCamera, Close } from '@mui/icons-material';
import { dashboardAPI, pendingStudentAPI, classesAPI, photoUploadAPI } from '../../services/api';

const getUploadablePhoto = async (photoInput) => {
  if (typeof photoInput === 'string' && photoInput.startsWith('data:')) {
    const response = await fetch(photoInput);
    return response.blob();
  }

  return photoInput;
};

const MyClasses = () => {
  const [stats, setStats] = useState({ totalClasses: 0, totalStudents: 0, myClasses: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAddStudent, setOpenAddStudent] = useState(false);
  const [pendingStudents, setPendingStudents] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [submittingStudent, setSubmittingStudent] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    student_name: '',
    standard: '',
    section: 'A',
    phone_number: '',
    photo_path: null,
  });

  useEffect(() => {
    const loadClasses = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await dashboardAPI.getTeacherStats();
        setStats({
          totalClasses: response.data.totalClasses || 0,
          totalStudents: response.data.totalStudents || 0,
          myClasses: Array.isArray(response.data.myClasses) ? response.data.myClasses : [],
        });
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load classes');
      } finally {
        setLoading(false);
      }
    };

    loadClasses();
  }, []);

  useEffect(() => {
    const loadPendingStudents = async () => {
      try {
        setLoadingPending(true);
        const response = await pendingStudentAPI.getAll();
        setPendingStudents(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to load pending students:', err);
      } finally {
        setLoadingPending(false);
      }
    };

    loadPendingStudents();
  }, []);

  useEffect(() => {
    const loadClasses = async () => {
      try {
        setLoadingClasses(true);
        const response = await classesAPI.getAll();
        const classes = Array.isArray(response.data) ? response.data : [];
        setAvailableClasses(classes);
      } catch (err) {
        console.error('Failed to load classes:', err);
        setAvailableClasses([]);
      } finally {
        setLoadingClasses(false);
      }
    };

    loadClasses();
  }, []);

  const handleAddStudentClick = () => {
    setOpenAddStudent(true);
    setSubmitError('');
    setSubmitSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenAddStudent(false);
    setFormData({
      student_name: '',
      standard: '',
      section: 'A',
      phone_number: '',
      photo_path: null,
    });
    setPhotoPreview(null);
    setSubmitError('');
    setSubmitSuccess('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const openCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotoPreview(event.target.result);
        setFormData(prev => ({
          ...prev,
          photo_path: file,
        }));
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setFormData(prev => ({
      ...prev,
      photo_path: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmitStudent = async () => {
    setSubmitError('');
    setSubmitSuccess('');

    if (!formData.student_name.trim()) {
      setSubmitError('Student name is required');
      return;
    }

    if (!formData.standard) {
      setSubmitError('Standard is required');
      return;
    }

    try {
      setSubmittingStudent(true);
      const submitData = { ...formData };

      if (formData.photo_path) {
        const photoFile = await getUploadablePhoto(formData.photo_path);
        const photoResponse = await photoUploadAPI.upload(photoFile);
        submitData.photo_path = photoResponse.data.photo_path;
      } else {
        submitData.photo_path = null;
      }

      const response = await pendingStudentAPI.submit(submitData);
      setSubmitSuccess(response.data.message || 'Student submitted successfully');
      
      // Refresh pending students list
      const updatedPending = await pendingStudentAPI.getAll();
      setPendingStudents(Array.isArray(updatedPending.data) ? updatedPending.data : []);

      // Close dialog after 2 seconds
      setTimeout(() => {
        handleCloseDialog();
      }, 2000);
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Failed to submit student');
    } finally {
      setSubmittingStudent(false);
    }
  };

  const handleDeletePending = async (id) => {
    if (window.confirm('Are you sure you want to delete this pending request?')) {
      try {
        await pendingStudentAPI.delete(id);
        
        // Refresh pending students list
        const updatedPending = await pendingStudentAPI.getAll();
        setPendingStudents(Array.isArray(updatedPending.data) ? updatedPending.data : []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete request');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        My Classes
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Paper>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <School color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Assigned Classes</Typography>
                  <Typography variant="h5" fontWeight="bold">{stats.totalClasses}</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <People color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Students In My Classes</Typography>
                  <Typography variant="h5" fontWeight="bold">{stats.totalStudents}</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Class List</Typography>
              {stats.myClasses.length > 0 ? (
                <Grid container spacing={2}>
                  {stats.myClasses.map((cls, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`${cls.standard}-${cls.section}-${index}`}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6">Class {cls.standard}</Typography>
                        <Typography color="text.secondary">Section {cls.section || '-'}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="text.secondary">No classes assigned yet.</Typography>
              )}
            </Paper>
          </Grid>

          {/* Add Student Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6">Add Students to Class</Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddStudentClick}
                  color="primary"
                >
                  Add Student
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Submit student information for admin approval. Once approved, the student will be added to the main student list.
              </Typography>
            </Paper>
          </Grid>

          {/* Pending Students List */}
          {pendingStudents.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Submitted Students for Approval</Typography>
                {loadingPending ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell>Student Name</TableCell>
                          <TableCell>Standard</TableCell>
                          <TableCell>Section</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Submitted On</TableCell>
                          <TableCell>Admin Comments</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingStudents.map((student) => (
                          <TableRow key={student.id}>
                            <TableCell>{student.student_name}</TableCell>
                            <TableCell>{student.standard}</TableCell>
                            <TableCell>{student.section}</TableCell>
                            <TableCell>{student.phone_number || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={student.status}
                                color={getStatusColor(student.status)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{new Date(student.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>{student.admin_comments || '-'}</TableCell>
                            <TableCell align="center">
                              {student.status === 'PENDING' && (
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<Delete />}
                                  onClick={() => handleDeletePending(student.id)}
                                >
                                  Delete
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Add Student Dialog */}
      <Dialog open={openAddStudent} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Student for Approval</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {submitSuccess}
            </Alert>
          )}
          
          <TextField
            autoFocus
            fullWidth
            label="Student Name"
            name="student_name"
            value={formData.student_name}
            onChange={handleFormChange}
            margin="normal"
            placeholder="Enter full name"
            required
          />
          <TextField
            select
            fullWidth
            label="Standard/Class"
            name="standard"
            value={formData.standard}
            onChange={handleFormChange}
            margin="normal"
            required
            disabled={loadingClasses}
          >
            {loadingClasses ? (
              <MenuItem disabled>Loading classes...</MenuItem>
            ) : availableClasses.length > 0 ? (
              availableClasses.map((cls) => (
                <MenuItem key={cls.id} value={cls.standard}>
                  {cls.display_name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No classes available</MenuItem>
            )}
          </TextField>
          <TextField
            select
            fullWidth
            label="Section"
            name="section"
            value={formData.section}
            onChange={handleFormChange}
            margin="normal"
          >
            <MenuItem value="A">A</MenuItem>
            <MenuItem value="B">B</MenuItem>
            <MenuItem value="C">C</MenuItem>
            <MenuItem value="D">D</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Mobile Number"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleFormChange}
            margin="normal"
            placeholder="Enter 10-digit mobile number"
            type="tel"
          />

          {/* Photo Upload Section */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Student Photo
            </Typography>
            
            {photoPreview && (
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Card>
                  <CardMedia
                    component="img"
                    image={photoPreview}
                    alt="Student photo preview"
                    sx={{ height: 200, objectFit: 'cover' }}
                  />
                </Card>
                <IconButton
                  onClick={removePhoto}
                  sx={{
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={openCamera}
                fullWidth
              >
                Capture from Camera
              </Button>
              <Button
                variant="outlined"
                onClick={openFilePicker}
                fullWidth
              >
                Choose from Gallery
              </Button>
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoFileChange}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handlePhotoFileChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submittingStudent}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitStudent}
            variant="contained"
            color="primary"
            disabled={submittingStudent}
          >
            {submittingStudent ? <CircularProgress size={20} /> : 'Submit for Approval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyClasses;
